/**
 * AOI Calculator — 7 parameter categories (shared by district Calculator + Location tab).
 */
export const AOI_CATEGORIES = [
    {
        key: 'affluentDensity',
        short: 'AD',
        title: 'Affluent Density',
        accent: 'bg-violet-50 border-violet-200 text-violet-900',
        parentWeight: 25,
        subs: [
            { key: 'hni', label: 'HNI / UHNI score', weight: 35 },
            { key: 'premiumHousing', label: 'Premium housing', weight: 25 },
            { key: 'luxuryConsumption', label: 'Luxury consumption', weight: 20 },
            { key: 'premiumRetail', label: 'Premium retail presence', weight: 20 },
        ],
    },
    {
        key: 'wellnessMaturity',
        short: 'WM',
        title: 'Wellness Maturity',
        accent: 'bg-sky-50 border-sky-200 text-sky-900',
        parentWeight: 20,
        subs: [
            { key: 'premiumGyms', label: 'Premium gym density', weight: 30 },
            { key: 'diagnostics', label: 'Diagnostics penetration', weight: 25 },
            { key: 'wellnessSpend', label: 'Wellness spend culture', weight: 25 },
            { key: 'recoveryAesthetic', label: 'Recovery / aesthetic ecosystem', weight: 20 },
        ],
    },
    {
        key: 'urbanCatchment',
        short: 'UC',
        title: 'Urban Catchment',
        accent: 'bg-emerald-50 border-emerald-200 text-emerald-900',
        parentWeight: 15,
        subs: [
            { key: 'urbanDensity', label: 'Urban density', weight: 40 },
            { key: 'premiumResidential', label: 'Premium residential', weight: 35 },
            { key: 'commercialActivity', label: 'Commercial activity', weight: 25 },
        ],
    },
    {
        key: 'corporateEcosystem',
        short: 'CE',
        title: 'Corporate Ecosystem',
        accent: 'bg-amber-50 border-amber-200 text-amber-900',
        parentWeight: 15,
        subs: [
            { key: 'corporateDensity', label: 'Corporate density', weight: 40 },
            { key: 'professionalPop', label: 'Professional population', weight: 35 },
            { key: 'businessActivity', label: 'Business activity', weight: 25 },
        ],
    },
    {
        key: 'healthcareAwareness',
        short: 'HA',
        title: 'Healthcare Awareness',
        accent: 'bg-rose-50 border-rose-200 text-rose-900',
        parentWeight: 10,
        subs: [
            { key: 'hospitalEcosystem', label: 'Hospital ecosystem', weight: 40 },
            { key: 'diagnosticsAdoption', label: 'Diagnostics adoption', weight: 35 },
            { key: 'preventiveHealthcare', label: 'Preventive healthcare', weight: 25 },
        ],
    },
    {
        key: 'lifestyleDisease',
        short: 'LD',
        title: 'Lifestyle Disease Burden',
        accent: 'bg-orange-50 border-orange-200 text-orange-900',
        parentWeight: 10,
        subs: [
            { key: 'obesityDiabetes', label: 'Obesity / diabetes prevalence', weight: 40 },
            { key: 'stressSedentary', label: 'Stress / sedentary population', weight: 35 },
            { key: 'metabolicDisease', label: 'Metabolic disease indicators', weight: 25 },
        ],
    },
    {
        key: 'competitiveWhitespace',
        short: 'CW',
        title: 'Competitive Whitespace',
        accent: 'bg-cyan-50 border-cyan-200 text-cyan-900',
        parentWeight: 5,
        subs: [
            { key: 'whitespaceScore', label: 'Normalised whitespace score', weight: 100 },
        ],
    },
];

export function emptyAoiInputs() {
    const o = {};
    for (const c of AOI_CATEGORIES) {
        o[c.key] = {};
        for (const s of c.subs) o[c.key][s.key] = 0;
    }
    return o;
}

export function aoiScoreTone(s) {
    if (s >= 80) return 'bg-emerald-100 text-emerald-800';
    if (s >= 60) return 'bg-brand-teal-50 text-brand-teal-800';
    if (s >= 40) return 'bg-amber-100 text-amber-800';
    if (s >= 20) return 'bg-orange-100 text-orange-800';
    return 'bg-rose-100 text-rose-800';
}

export const SEARCH_RADIUS_PRESETS = [
    { id: '500m', label: '500 m', km: 0.5 },
    { id: '1km', label: '1 km', km: 1 },
    { id: '1500m', label: '1.5 km', km: 1.5 },
    { id: '2km', label: '2 km', km: 2 },
    { id: '3km', label: '3 km', km: 3 },
    { id: '5km', label: '5 km', km: 5 },
    { id: 'manual', label: 'Custom', km: null },
];

const SEARCH_RADIUS_MODE_ALIASES = {
    '1.5km': '1500m',
};

export function formatSearchRadius(km) {
    if (km < 1) return `${Math.round(km * 1000)} m`;
    return Number.isInteger(km) ? `${km} km` : `${km.toFixed(1)} km`;
}

export function searchRadiusPresetLabel(preset) {
    if (!preset) return 'Custom';
    if (preset.label) return preset.label;
    if (preset.id === 'manual' || preset.km == null) return 'Custom';
    return formatSearchRadius(preset.km);
}

export function effectiveSearchRadiusKm(mode, manualKm) {
    const resolvedMode = SEARCH_RADIUS_MODE_ALIASES[mode] || mode;
    if (resolvedMode === 'manual') {
        const n = Number(manualKm);
        if (!Number.isFinite(n) || n <= 0) return 2;
        return Math.min(25, Math.max(0.2, n));
    }
    return SEARCH_RADIUS_PRESETS.find(p => p.id === resolvedMode)?.km ?? 2;
}

export function countEmptyAoiMerge(prev, suggestions) {
    let n = 0;
    for (const [cat, subs] of Object.entries(suggestions || {})) {
        for (const [k, v] of Object.entries(subs || {})) {
            if (!prev[cat]?.[k] && v > 0) n += 1;
        }
    }
    return n;
}

/** AOI = Σ (category score × parent weight ÷ 100). */
export function computeAoiContributions(categoryScores = {}) {
    const rows = AOI_CATEGORIES.map(cat => {
        const score = Number(categoryScores[cat.key]) || 0;
        const contribution = score * (cat.parentWeight / 100);
        return {
            key: cat.key,
            short: cat.short,
            title: cat.title,
            weight: cat.parentWeight,
            score,
            contribution,
        };
    });
    const total = rows.reduce((sum, r) => sum + r.contribution, 0);
    return { rows, total: Math.round(total * 100) / 100 };
}

export const AOI_FINAL_FORMULA =
    'AOI Score = Σ (Category Score × Parent Weight) = AD × 25% + WM × 20% + UC × 15% + CE × 15% + HA × 10% + LD × 10% + CW × 5%';
